<template>
    <div v-if="authStore.isAuthenticated">
        <div v-if="isSuperAdmin" class="module baremes-management">
            <div class="top-bar">
                <h2 class="page-title">Barèmes de points</h2>
                <div class="top-controls">
                    <select v-model.number="seasonId" class="form-input-sm">
                        <option v-for="s in seasons" :value="s.id" :key="s.id">
                            {{ s.label }}{{ s.is_current ? ' (en cours)' : '' }}
                        </option>
                    </select>
                    <button type="button" class="btn-secondary" :disabled="duplicating || !previousSeasonId" @click="duplicateAllFromPreviousSeason">
                        Dupliquer depuis {{ previousSeasonLabel || 'la saison préc.' }}
                    </button>
                    <button type="button" class="btn-submit" :disabled="saving" @click="saveAll">
                        {{ saving ? '...' : 'Enregistrer' }}
                    </button>
                </div>
            </div>
            <p class="hint">Même valeur pour toutes les catégories (Masculin/Féminin/Vétéran/Junior) -- une seule case par palier.</p>

            <div v-if="loading" class="hint">Chargement...</div>
            <p v-else-if="loadError" class="load-error">⚠️ {{ loadError }}</p>

            <template v-else>
                <table class="full-table">
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th>Format</th>
                            <th v-for="col in POSITION_COLUMNS" :key="col.code">{{ col.label }}</th>
                            <th v-for="col in POULES_COLUMNS" :key="col.code">{{ col.label }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="type in bracketTypes" :key="type.code">
                            <tr v-for="(fmt, fmtIdx) in FORMATS" :key="fmt.key">
                                <td v-if="fmtIdx === 0" :rowspan="FORMATS.length" class="type-cell">
                                    {{ type.label }} <span class="type-code">({{ type.code }})</span>
                                </td>
                                <td class="format-cell">{{ fmt.label }}</td>
                                <td v-for="col in POSITION_COLUMNS" :key="col.code">
                                    <input
                                        v-if="fmt.codeFor(col.code)"
                                        v-model.number="type.grid[fmt.codeFor(col.code)!]"
                                        type="number" step="0.5" class="cell-input"
                                    />
                                    <span v-else class="cell-na">—</span>
                                </td>
                                <td v-for="col in POULES_COLUMNS" :key="col.code">
                                    <input
                                        v-if="fmt.key === 'rr'"
                                        v-model.number="type.grid[col.code]"
                                        type="number" step="0.5" class="cell-input"
                                    />
                                    <span v-else class="cell-na">—</span>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>

                <!-- Classements par position (pas de format tableau/poules -- une place =
                     un nombre de points, cf. discussion du 2026-09-20 : IC, COM, CL_FED). -->
                <div v-if="rankTypes.length" class="rank-block">
                    <h4>Classements par position</h4>
                    <table class="full-table">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th v-for="rank in maxRankColumns" :key="rank">{{ rank }}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="type in rankTypes" :key="type.code">
                                <td class="type-cell">{{ type.label }} <span class="type-code">({{ type.code }})</span></td>
                                <td v-for="rank in maxRankColumns" :key="rank">
                                    <input v-model.number="type.grid[String(rank)]" type="number" step="0.5" class="cell-input" />
                                </td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                    <button type="button" class="btn-secondary btn-xs" @click="maxRankColumns++">
                        + Ajouter une position ({{ maxRankColumns + 1 }})
                    </button>
                </div>

                <details class="advanced-block">
                    <summary>Codes avancés <span class="hint-inline">(cas particuliers hors grille standard, ex. codes de poules mixtes "16/P3")</span></summary>
                    <div v-for="type in types" class="advanced-type" :key="type.code">
                        <strong>{{ type.label }} ({{ type.code }})</strong>
                        <table v-if="type.customRows.length" class="advanced-table">
                            <thead>
                                <tr><th>Code</th><th>Points</th><th></th></tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, idx) in type.customRows" :key="idx">
                                    <td><input v-model="row.code_clt" class="cell-input code-input" placeholder="ex : 16/P3" /></td>
                                    <td><input v-model.number="row.points" type="number" step="0.5" class="cell-input" /></td>
                                    <td><button type="button" class="row-delete" @click="type.customRows.splice(idx, 1)">×</button></td>
                                </tr>
                            </tbody>
                        </table>
                        <button type="button" class="btn-secondary btn-xs" @click="type.customRows.push({ code_clt: '', points: null })">
                            + Ajouter un code
                        </button>
                    </div>
                </details>
            </template>
        </div>

        <div v-else class="centered-block">
            <p>Cette section est réservée aux super administrateurs.</p>
            <router-link to="/" class="cta-link">← Retour à l'accueil</router-link>
        </div>
    </div>

    <div v-else class="centered-block">
        <h2>Connexion requise</h2>
        <p>Veuillez vous connecter pour accéder à cette section.</p>
        <button class="btn-primary" @click="authStore.openAuthModal">Connexion</button>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import dartsHubApi from '@/axios/dartsHubApi'
import { handleError } from '@/functions/utils'
import { useAuthStore } from '@/stores/useAuthStore'

const authStore = useAuthStore()
const isSuperAdmin = computed(() => authStore.scopes.includes('super_admin'))

interface SeasonOption {
    id: number
    label: string
    is_current: boolean
    start_date: string
}
interface SourceTypeOption {
    code: string
    label: string
    kind: 'imported' | 'derived' | 'legacy_manual'
}

// Classements par position (pas de format tableau/poules) -- IC (imported, mais rang-par-
// rang) + COM/CL_FED/CT_FED (derived, reconversion de position -- code_clt = rang exact,
// cf. compute_ranking). Tout le reste (OC/CC/OL/CL/MASTER/OF/CN/CF/MS) est "en tableau"
// (Round Robin/Tournoi A/B + paliers de position), cf. discussion du 2026-09-20.
const RANK_SHAPED_CODES = new Set(['IC', 'COM', 'CL_FED', 'CT_FED'])

// Colonnes = paliers de classement, alignés sur le tableau FFD officiel fourni par
// l'utilisateur (Vainqueur/Finaliste/Demi/Quarts/8èmes/16èmes/32èmes/64èmes/126èmes) --
// cf. Dartshub/backend/modules/api/leaderboards/functions.py::get_code_clt, étendu le
// 2026-09-20 pour couvrir tous les paliers jusqu'à 512 joueurs (plus de plafond à "17").
const POSITION_COLUMNS = [
    { code: '1', label: 'Vainqueur' },
    { code: '2', label: 'Finaliste' },
    { code: '3', label: 'Demi' },
    { code: '5', label: 'Quarts' },
    { code: '9', label: '8èmes' },
    { code: '17', label: '16èmes' },
    { code: '33', label: '32èmes' },
    { code: '65', label: '64èmes' },
    { code: '137', label: '126èmes' },
]
const POULES_COLUMNS = [
    { code: 'P4', label: '4 Vict.' },
    { code: 'P3', label: '3 Vict.' },
    { code: 'P2', label: '2 Vict.' },
    { code: 'P1', label: '1 Vict.' },
    { code: 'P0', label: '0 Vict.' },
]

// Lignes = un FORMAT par type (Round Robin / Tournoi A / Tournoi B), comme le tableau
// officiel. `codeFor` traduit une colonne "palier" en code_clt réel pour ce format --
// Tournoi B (cf. get_code_clt) s'arrête à "8èmes" (B9), pas de 16èmes et plus, ni de poules.
const FORMATS = [
    { key: 'rr', label: 'Round Robin', codeFor: (col: string) => col },
    { key: 'a', label: 'Tournoi A', codeFor: (col: string) => `A${col}` },
    {
        key: 'b', label: 'Tournoi B',
        codeFor: (col: string) => (['1', '2', '3', '5'].includes(col) ? `B${col}` : col === '9' ? 'B9' : null),
    },
]
const ALL_STANDARD_CODES = new Set([
    ...POSITION_COLUMNS.map((c) => c.code),
    ...POSITION_COLUMNS.map((c) => `A${c.code}`),
    'B1', 'B2', 'B3', 'B5', 'B9',
    ...POULES_COLUMNS.map((c) => c.code),
])

interface CustomRow {
    code_clt: string
    points: number | null
}
interface TypeState {
    code: string
    label: string
    level: 'comite' | 'ligue' | 'federal'
    rankShaped: boolean
    grid: Record<string, number | null>
    customRows: CustomRow[]
}

// National en premier, comité en dernier -- même ordre que le tableau de référence fourni.
const LEVELS: { level: 'federal' | 'ligue' | 'comite' }[] = [{ level: 'federal' }, { level: 'ligue' }, { level: 'comite' }]

const seasons = ref<SeasonOption[]>([])
const seasonId = ref<number | null>(null)
const types = ref<TypeState[]>([])
const maxRankColumns = ref(18)
const loading = ref(false)
const saving = ref(false)
const duplicating = ref(false)
const loadError = ref<string | null>(null)

const bracketTypes = computed(() => types.value.filter((t) => !t.rankShaped))
const rankTypes = computed(() => types.value.filter((t) => t.rankShaped))

function silentError(err: unknown, context: string): void {
    console.error(`Error while ${context}`, err)
    const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
    loadError.value = detail ? `${context} : ${detail}` : `Erreur lors du ${context}.`
}

const previousSeasonId = computed(() => {
    if (!seasonId.value) return null
    const sorted = [...seasons.value].sort((a, b) => a.start_date.localeCompare(b.start_date))
    const idx = sorted.findIndex((s) => s.id === seasonId.value)
    return idx > 0 ? sorted[idx - 1].id : null
})
const previousSeasonLabel = computed(() => seasons.value.find((s) => s.id === previousSeasonId.value)?.label ?? '')

async function fetchSeasons() {
    try {
        const { data } = await dartsHubApi.get('/seasons')
        seasons.value = data
        seasonId.value = data.find((s: SeasonOption) => s.is_current)?.id ?? data[0]?.id ?? null
    } catch (err) {
        silentError(err, 'chargement des saisons')
    }
}

async function fetchAll() {
    if (!seasonId.value) return
    loading.value = true
    loadError.value = null
    try {
        const perLevel = await Promise.all(
            LEVELS.map(({ level }) =>
                dartsHubApi.get('/leaderboard/source-types', { params: { level, season_id: seasonId.value, include_derived: true } })
                    .then((res) => ({ level, list: res.data as SourceTypeOption[] }))
            )
        )
        const newTypes: TypeState[] = []
        for (const { level, list } of perLevel) {
            for (const t of list) {
                newTypes.push({
                    code: t.code, label: t.label, level,
                    rankShaped: RANK_SHAPED_CODES.has(t.code),
                    grid: {}, customRows: [],
                })
            }
        }

        await Promise.all(newTypes.map(async (type) => {
            const { data } = await dartsHubApi.get('/leaderboard/points-scale', {
                params: { scope_level: type.level, season_id: seasonId.value, source_type_code: type.code },
            })
            // Même valeur pour toutes les catégories -- on ne garde que la première
            // rencontrée par code_clt (elles sont identiques, cf. consigne utilisateur du
            // 2026-09-20 : "le barème de l'image est le même pour toutes les catégories").
            const byCode = new Map<string, number>()
            for (const r of data as { category: string; code_clt: string; points: number }[]) {
                if (!byCode.has(r.code_clt)) byCode.set(r.code_clt, r.points)
            }
            for (const [code, points] of byCode) {
                if (type.rankShaped || ALL_STANDARD_CODES.has(code)) {
                    type.grid[code] = points
                    if (type.rankShaped) {
                        const rankNum = Number(code)
                        if (Number.isFinite(rankNum)) maxRankColumns.value = Math.max(maxRankColumns.value, rankNum)
                    }
                } else {
                    type.customRows.push({ code_clt: code, points })
                }
            }
        }))

        types.value = newTypes
    } catch (err) {
        silentError(err, 'chargement du barème')
    } finally {
        loading.value = false
    }
}

function buildPayloadRows(type: TypeState) {
    const CATEGORIES = ['M', 'F', 'V', 'J'] as const
    const payload: { category: string; code_clt: string; points: number }[] = []
    const codes = type.rankShaped
        ? Array.from({ length: maxRankColumns.value }, (_, i) => String(i + 1))
        : Array.from(ALL_STANDARD_CODES)
    for (const code of codes) {
        const points = type.grid[code]
        if (points !== null && points !== undefined) {
            for (const cat of CATEGORIES) payload.push({ category: cat, code_clt: code, points })
        }
    }
    for (const row of type.customRows) {
        if (!row.code_clt.trim() || row.points === null || row.points === undefined) continue
        for (const cat of CATEGORIES) payload.push({ category: cat, code_clt: row.code_clt.trim(), points: row.points })
    }
    return payload
}

async function saveAll() {
    if (!seasonId.value) return
    saving.value = true
    try {
        await Promise.all(types.value.map((type) => dartsHubApi.put('/leaderboard/points-scale', {
            scope_level: type.level, season_id: seasonId.value, source_type_code: type.code,
            rows: buildPayloadRows(type),
        })))
        toast.success('Barème enregistré.')
    } catch (err) {
        handleError(err, 'enregistrement du barème')
    } finally {
        saving.value = false
    }
}

async function duplicateAllFromPreviousSeason() {
    if (!previousSeasonId.value || !seasonId.value) return
    duplicating.value = true
    try {
        const results = await Promise.all(types.value.map((type) =>
            dartsHubApi.post('/leaderboard/points-scale/duplicate', {
                scope_level: type.level, from_season_id: previousSeasonId.value, to_season_id: seasonId.value,
                source_type_code: type.code,
            }).then((res) => res.data.rows_created as number).catch(() => 0)
        ))
        toast.success(`${results.reduce((a, b) => a + b, 0)} ligne(s) copiée(s).`)
        await fetchAll()
    } catch (err) {
        handleError(err, 'duplication du barème')
    } finally {
        duplicating.value = false
    }
}

onMounted(fetchSeasons)
watch(seasonId, fetchAll)
</script>

<style scoped>
.baremes-management {
    /* Casse le plafond 1200px du layout parent (.app-content, App.vue) -- la grille a
       besoin de toute la largeur de fenêtre pour rester lisible (9 paliers + 5 poules +
       plusieurs formats par type), cf. consigne utilisateur du 2026-09-20. */
    max-width: none;
    width: 100vw;
    margin-left: calc(50% - 50vw);
    padding: 0 1.5rem;
    box-sizing: border-box;
}

.top-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
}

.page-title {
    margin: 0;
    font-size: 1.1rem;
}

.top-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.hint {
    font-size: 0.85rem;
    color: var(--color-fg-muted, #888);
    margin: 0 0 0.5rem;
}

.hint-inline {
    font-weight: 400;
    font-size: 0.78rem;
    color: var(--color-fg-muted, #888);
}

.load-error {
    font-size: 0.9rem;
    color: var(--color-danger, #d9534f);
}

.full-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.95rem;
    table-layout: fixed;
}

.full-table th,
.full-table td {
    border: 1px solid var(--color-light-shadow, #ddd);
    padding: 0.4rem 0.3rem;
    text-align: center;
}

.full-table th {
    background: var(--color-bg-soft, rgba(0, 0, 0, 0.04));
    font-size: 0.85rem;
}

.type-cell {
    text-align: left;
    font-weight: 700;
    font-size: 0.9rem;
    vertical-align: middle;
    width: 15%;
}

.type-code {
    font-weight: 400;
    color: var(--color-fg-muted, #888);
}

.format-cell {
    text-align: left;
    font-size: 0.9rem;
    white-space: nowrap;
    width: 9%;
}

.cell-input {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    padding: 0.3rem 0.2rem;
    border: none;
    background: transparent;
    text-align: center;
    font-size: 0.95rem;
    line-height: 1.3;
    /* Cache les flèches +/- des <input type="number"> -- elles rognaient la place
       disponible pour le chiffre lui-même dans une case déjà étroite. */
    -moz-appearance: textfield;
    appearance: textfield;
}

.cell-input::-webkit-outer-spin-button,
.cell-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.cell-input:focus {
    outline: 1px solid var(--color-accent, #1a3a5c);
    background: var(--color-bg, #fff);
}

.cell-na {
    color: var(--color-light-shadow, #ccc);
}

.rank-block {
    margin-top: 1.25rem;
}

.rank-block h4 {
    margin-bottom: 0.4rem;
    font-size: 0.95rem;
}

.advanced-block {
    margin-top: 0.75rem;
    font-size: 0.85rem;
}

.advanced-type {
    margin: 0.5rem 0;
}

.advanced-table {
    border-collapse: collapse;
    font-size: 0.8rem;
    margin: 0.35rem 0;
}

.advanced-table th,
.advanced-table td {
    border: 1px solid var(--color-light-shadow, #eee);
    padding: 0.2rem 0.4rem;
}

.code-input {
    max-width: 90px;
}

.row-delete {
    border: none;
    background: transparent;
    cursor: pointer;
    color: var(--color-danger, #d9534f);
    font-size: 1rem;
}

.btn-submit,
.btn-secondary {
    display: inline-flex;
    align-items: center;
    padding: 0.4rem 0.9rem;
    border-radius: var(--radius, 6px);
    font-weight: 600;
    cursor: pointer;
    border: 1px solid var(--color-accent, #1a3a5c);
    font-size: 0.85rem;
}

.btn-xs {
    padding: 0.2rem 0.5rem;
    font-size: 0.78rem;
}

.btn-submit {
    background: var(--color-accent, #1a3a5c);
    color: #fff;
}

.btn-submit:disabled,
.btn-secondary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-secondary {
    background: transparent;
    color: var(--color-accent, #1a3a5c);
}

.form-input-sm {
    padding: 0.3rem 0.5rem;
    border: 1px solid var(--color-light-shadow, #ddd);
    border-radius: 4px;
    font-size: 0.85rem;
}

.centered-block {
    text-align: center;
    padding: 3rem 1rem;
}
</style>
